import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useState } from 'react';

import type { Async } from '@client/new/helpers/types';

import { AutomationConfigField, copyFor } from '../automation-copy';
import {
  AutomationStepPreview,
  CreatorAutomationConfig,
  UpdateAutomationConfigRequest,
} from '../automations.api';
import { formatPriceCents } from '../automations.format';
import { useAutomationStepsModel } from '../model/AutomationSteps.model';
import {
  AutomationsUpdateError,
  useAutomationsModel,
} from '../model/Automations.model';
import {
  AutomationConfigDraft,
  AutomationConfigValidation,
  toConfigRequest,
  validateConfigDraft,
} from './Automations.viewModel';

export type TimelineRow = {
  key: string;
  timing: string;
  subject: string;
  preview: string;
  channel: 'email' | 'push';
  channelLabel: string;
};

export type Timeline =
  | { type: 'loading' }
  | { type: 'error'; message: string; retry: () => Promise<void> }
  | { type: 'ready'; rows: TimelineRow[] };

/** What the form slot edits. Everything is staged until Save. */
export type DetailsDraft = {
  enabled: boolean;
  includePushes: boolean;
  config: AutomationConfigDraft;
};

export type AutomationDetailsViewModel = {
  automationType: string;
  displayName: string;
  description: string;
  triggerLine: string;
  cadence: string;
  icon: IconProp;
  configFields: readonly AutomationConfigField[];
  requiredConfigFields: readonly AutomationConfigField[];
  /** Server's enable-gate reason for the saved state; null when ready. Never rendered, see `setupHint`. */
  readinessReason: string | null;
  /** Creator-facing hint under "What we need from you" while the type is not ready (GRW-821). */
  setupHint: string;
  /** Admin and moderator sessions get the send-test tool inside the modal. */
  adminTools: boolean;
  /** Resolved price pair for monthly_to_annual (cents), null when unresolvable or another type. */
  monthlyPrice: number | null;
  annualPrice: number | null;
  draft: DetailsDraft;
  validation: AutomationConfigValidation;
  /** Save when something can change, Done when there is nothing to set and nothing was touched. */
  primaryAction: 'save' | 'done';
  canSave: boolean;
  saving: boolean;
  error?: string;
  timeline: Timeline;
  setEnabled: (enabled: boolean) => void;
  setIncludePushes: (includePushes: boolean) => void;
  setConfig: (patch: Partial<AutomationConfigDraft>) => void;
  /** Sends one PATCH. Resolves true when accepted, so the View can close. */
  save: () => Promise<boolean>;
};

type DetailsError = { message: string; retry?: () => Promise<void> };

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * Figma reads step timing as prose. Enroll-anchored flows count from the
 * trigger ("Right away", "30 minutes later", "Day 3"); renewal-anchored ones
 * start a fixed week before the renewal and then step relative to the
 * previous message ("7 days before renewal", "1 day later").
 */
const CHANNEL_LABEL = { email: 'Email', push: 'Push' } as const;

export const timingLabels = (
  steps: Pick<AutomationStepPreview, 'delayMinutes' | 'anchor'>[],
): string[] => {
  let elapsed = 0;
  return steps.map((step, index) => {
    elapsed += step.delayMinutes;
    if (step.anchor === 'renewal') {
      if (index === 0) return '7 days before renewal';
      return `${relativeSpan(step.delayMinutes)} later`;
    }
    if (elapsed === 0) return 'Right away';
    if (elapsed < MINUTES_PER_DAY) return `${relativeSpan(elapsed)} later`;
    return `Day ${Math.round(elapsed / MINUTES_PER_DAY)}`;
  });
};

const relativeSpan = (minutes: number): string => {
  if (minutes < MINUTES_PER_HOUR) return plural(minutes, 'minute');
  if (minutes < MINUTES_PER_DAY) {
    return plural(Math.round(minutes / MINUTES_PER_HOUR), 'hour');
  }
  return plural(Math.round(minutes / MINUTES_PER_DAY), 'day');
};

/**
 * Subjects and previews come back as templates ("save {{savingsPercent}}").
 * The real values are per member, so the preview fills what the creator's
 * own data already tells us (the monthly→annual saving) and marks the rest
 * the way the Figma does ("X%", "[first name]").
 */
export const fillTemplate = (
  text: string,
  values: Record<string, string>,
): string =>
  text.replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_match, name: string) =>
      values[name] ?? TEMPLATE_PLACEHOLDERS[name] ?? `[${name}]`,
  );

const TEMPLATE_PLACEHOLDERS: Record<string, string> = {
  firstName: '[first name]',
  creatorName: '[your name]',
  productName: '[program name]',
  otpTitle: '[product name]',
  subscriptionTitle: '[your subscription]',
  priceFormatted: '[price]',
  renewalDate: '[renewal date]',
  savingsPercent: 'X%',
  annualSavings: '$X',
  monthlyRate: '$X/month',
  annualEffectiveRate: '$X/month',
};

/** Values known from the creator's prices (monthly_to_annual). */
export const templateValuesFromPrices = (
  monthlyPrice: number | null,
  annualPrice: number | null,
): Record<string, string> => {
  if (monthlyPrice === null || annualPrice === null) return {};
  const yearlyAtMonthly = monthlyPrice * 12;
  const saving = yearlyAtMonthly - annualPrice;
  if (saving <= 0) return {};
  return {
    savingsPercent: `${Math.round((saving / yearlyAtMonthly) * 100)}%`,
    annualSavings: formatPriceCents(saving),
    monthlyRate: `${formatPriceCents(monthlyPrice)}/month`,
    annualEffectiveRate: `${formatPriceCents(
      Math.round(annualPrice / 12),
    )}/month`,
  };
};

/** Drops push steps and re-bases each remaining delay on the previous kept
 *  step, so the timing labels still read right without the pushes. */
export const withoutPushes = (
  steps: AutomationStepPreview[],
): AutomationStepPreview[] => {
  let offset = 0;
  let lastKeptOffset = 0;
  const kept: AutomationStepPreview[] = [];
  for (const step of steps) {
    offset += step.delayMinutes;
    if (step.channel === 'push') continue;
    kept.push({
      ...step,
      delayMinutes:
        kept.length === 0 ? step.delayMinutes : offset - lastKeptOffset,
    });
    lastKeptOffset = offset;
  }
  return kept;
};

/** Mirrors messaging's `cadenceLineFor`: counts, then the span from the first
 *  to the last step in whole days. Used only when the creator turned pushes
 *  off, the server line covers the full sequence. */
export const cadenceLineFor = (steps: AutomationStepPreview[]): string => {
  const emails = steps.filter((step) => step.channel === 'email').length;
  const pushes = steps.filter((step) => step.channel === 'push').length;
  const counts = [
    emails > 0 ? plural(emails, 'email') : '',
    pushes > 0 ? `${pushes} ${pushes === 1 ? 'push' : 'pushes'}` : '',
  ]
    .filter((part) => part !== '')
    .join(' and ');
  if (steps.length <= 1) return counts;
  const spanMinutes = steps
    .slice(1)
    .reduce((sum, step) => sum + step.delayMinutes, 0);
  const spanDays = Math.max(1, Math.ceil(spanMinutes / MINUTES_PER_DAY));
  return `${counts} over ${plural(spanDays, 'day')}`;
};

export const toTimelineRows = (
  steps: AutomationStepPreview[],
  values: Record<string, string> = {},
): TimelineRow[] => {
  const labels = timingLabels(steps);
  return steps.map((step, index) => ({
    key: String(step.step),
    timing: labels[index],
    subject: fillTemplate(step.subject, values),
    preview: fillTemplate(step.preview, values),
    channel: step.channel,
    channelLabel: CHANNEL_LABEL[step.channel],
  }));
};

const savedDraft = (
  enabled: boolean,
  config: CreatorAutomationConfig | null,
): DetailsDraft => ({
  enabled,
  includePushes: config?.includePushes ?? true,
  config: {
    firstWorkoutId: config?.firstWorkout
      ? String(config.firstWorkout.workoutId)
      : '',
    otpPitchTargetProductId: config?.otpPitchTargetProductId ?? '',
  },
});

const REQUIRED_DRAFT_KEY: Partial<
  Record<AutomationConfigField, keyof AutomationConfigDraft>
> = {
  firstWorkout: 'firstWorkoutId',
  otpPitchTargetProductId: 'otpPitchTargetProductId',
};

const missingRequired = (
  draft: AutomationConfigDraft,
  required: readonly AutomationConfigField[],
): boolean =>
  required.some((field) => {
    const key = REQUIRED_DRAFT_KEY[field];
    return key !== undefined && draft[key] === '';
  });

const errorMessage = (failure: AutomationsUpdateError | undefined) => {
  if (!failure) return undefined;
  if (failure.kind === 'rejected') {
    return "That couldn't be saved. Check the details above and try again.";
  }
  return {
    unauthorized: 'Your session has expired. Sign in again and try once more.',
    forbidden: "You don't have permission to change this automation.",
    network: "We couldn't save your changes. Try again.",
  }[failure.kind];
};

export const useAutomationDetailsViewModel = (
  creatorId: number,
  automationType: string,
): Async<AutomationDetailsViewModel, DetailsError> => {
  const list = useAutomationsModel(creatorId);
  const steps = useAutomationStepsModel(creatorId, automationType);
  const [edits, setEdits] = useState<Partial<DetailsDraft>>({});

  if (list.type === 'fetch-error') {
    return {
      type: 'fetch-error',
      value: {
        message: "We couldn't load this automation.",
        retry: list.value.retry,
      },
    };
  }
  if (list.type !== 'fetched') return list;

  const item = list.value.automations.find(
    (automation) => automation.automationType === automationType,
  );
  if (!item) {
    return {
      type: 'fetch-error',
      value: { message: 'This automation is no longer available.' },
    };
  }

  const copy = copyFor(automationType);
  const saved = savedDraft(item.enabled, item.config);
  const draft: DetailsDraft = {
    ...saved,
    ...edits,
    config: { ...saved.config, ...edits.config },
  };
  const validation = validateConfigDraft(draft.config, copy.configFields);
  const configRequest = toConfigRequest(
    draft.config,
    saved.config,
    copy.configFields,
  );
  const pushesChanged =
    copy.configFields.includes('includePushes') &&
    draft.includePushes !== saved.includePushes;
  const enabledChanged = draft.enabled !== saved.enabled;
  const dirty =
    Object.keys(configRequest).length > 0 || pushesChanged || enabledChanged;
  const saving = list.value.pending.has(automationType);
  const blockedByRequired =
    draft.enabled && missingRequired(draft.config, copy.requiredConfigFields);

  const templateValues = templateValuesFromPrices(
    item.monthlyPrice ?? null,
    item.annualPrice ?? null,
  );
  const pushesOff =
    copy.configFields.includes('includePushes') && !draft.includePushes;
  const visibleSteps =
    steps.type === 'fetched'
      ? pushesOff
        ? withoutPushes(steps.value)
        : steps.value
      : null;
  const timeline: Timeline =
    visibleSteps !== null
      ? { type: 'ready', rows: toTimelineRows(visibleSteps, templateValues) }
      : steps.type === 'fetch-error'
      ? {
          type: 'error',
          message: "We couldn't load the messages for this automation.",
          retry: steps.value.retry,
        }
      : { type: 'loading' };

  return {
    type: 'fetched',
    value: {
      automationType,
      displayName: item.displayName,
      description: item.description,
      triggerLine: item.triggerLine,
      cadence:
        pushesOff && visibleSteps !== null
          ? cadenceLineFor(visibleSteps)
          : item.cadence,
      icon: copy.icon,
      configFields: copy.configFields,
      requiredConfigFields: copy.requiredConfigFields,
      readinessReason: item.readinessReason,
      setupHint:
        copy.configFields.length > 0 && !copy.serverReadinessCheck
          ? 'Fill in the required detail below before switching this on.'
          : copy.setupHint,
      adminTools: list.value.adminTools,
      monthlyPrice: item.monthlyPrice ?? null,
      annualPrice: item.annualPrice ?? null,
      draft,
      validation,
      primaryAction:
        copy.configFields.length === 0 && !enabledChanged ? 'done' : 'save',
      canSave:
        dirty &&
        !saving &&
        !blockedByRequired &&
        Object.keys(validation).length === 0,
      saving,
      error: errorMessage(list.value.errors[automationType]),
      timeline,
      setEnabled: (enabled) => setEdits((current) => ({ ...current, enabled })),
      setIncludePushes: (includePushes) =>
        setEdits((current) => ({ ...current, includePushes })),
      setConfig: (patch) =>
        setEdits((current) => ({
          ...current,
          config: { ...current.config, ...patch },
        })),
      save: async () => {
        const config: UpdateAutomationConfigRequest = {
          ...configRequest,
          ...(pushesChanged ? { includePushes: draft.includePushes } : {}),
        };
        const hasConfig = Object.keys(config).length > 0;
        const accepted = enabledChanged
          ? await list.value.update(
              automationType,
              draft.enabled,
              hasConfig ? config : undefined,
            )
          : hasConfig
          ? await list.value.saveConfig(automationType, config)
          : true;
        if (!accepted && draft.enabled) {
          // The gate refused: keep what was typed, drop the switch back.
          setEdits((current) => ({ ...current, enabled: false }));
        }
        if (accepted) setEdits({});
        return accepted;
      },
    },
  };
};
